import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as chatApi from '../../api/chat';
import type {
  ChatRoom,
  CreateRoomRequest,
  UpdateRoomRequest,
} from '../../types/chat';
import { chatKeys } from './chatKeys';

export function useCreateRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateRoomRequest) => chatApi.createRoom(req),
    onSuccess: (room: ChatRoom) => {
      qc.invalidateQueries({ queryKey: ['chat', 'rooms'] });
      qc.setQueryData(chatKeys.room(room.id), room);
    },
  });
}

export function useUpdateRoom(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: UpdateRoomRequest) => chatApi.updateRoom(roomId, req),
    onSuccess: (room: ChatRoom) => {
      qc.setQueryData(chatKeys.room(room.id), room);
      qc.invalidateQueries({ queryKey: ['chat', 'rooms'] });
    },
  });
}

export function useLeaveRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roomId: string) => chatApi.leaveRoom(roomId),
    onSuccess: (_void, roomId) => {
      qc.removeQueries({ queryKey: chatKeys.room(roomId) });
      qc.removeQueries({ queryKey: chatKeys.messages(roomId) });
      qc.invalidateQueries({ queryKey: ['chat', 'rooms'] });
      qc.invalidateQueries({ queryKey: chatKeys.unread() });
    },
  });
}

export function useAddMembers(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) => chatApi.addMembers(roomId, userIds),
    onSuccess: (room: ChatRoom) => {
      qc.setQueryData(chatKeys.room(room.id), room);
    },
  });
}

export function useRemoveMember(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => chatApi.removeMember(roomId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatKeys.room(roomId) });
    },
  });
}
